/**
 * Canonical public visibility + CTA rules.
 *
 * ONE place decides:
 *   - whether a class appears on the public Classes page / Calendar
 *   - which parent-facing and instructor-facing actions are offered
 *
 * Every public surface (Classes page, Calendar, ClassCard, ClassDetailDialog)
 * and the admin conflict banner must read from here. Never re-derive these
 * rules locally and never special-case an individual class.
 */

import type { ClassItem } from "@/data/classes";
import { STATUS_META, type RegistryEntry, type VisibilityStatus } from "@/lib/classRegistry";

/** Public status a ClassItem carries once registry status has been applied. */
export type PublicStatus = ClassItem["status"];

export interface PublicCta {
  /** Paid checkout / registration form may be shown. */
  register: boolean;
  /** Parent "I'm Interested for My Child" form may be shown. */
  interest: boolean;
  /** Prospective-instructor "Teach This Class" pathway may be shown. */
  teach: boolean;
  /** Waitlist wording instead of plain interest wording. */
  waitlist: boolean;
}

/**
 * Parent + instructor pathways for a public class state.
 *
 * Key rule: instructor recruitment and parent demand are INDEPENDENT.
 * An "Instructor Wanted" class still collects parent interest.
 */
export function publicCtaFor(status: PublicStatus, isExternal = false): PublicCta {
  if (isExternal) {
    return { register: true, interest: false, teach: false, waitlist: false };
  }
  switch (status) {
    case "open":
      return { register: true, interest: false, teach: false, waitlist: false };
    case "instructor-wanted":
      return { register: false, interest: true, teach: true, waitlist: false };
    case "coming-soon":
      return { register: false, interest: true, teach: false, waitlist: false };
    case "waitlist":
      return { register: false, interest: true, teach: false, waitlist: true };
    case "ended":
    default:
      return { register: false, interest: false, teach: false, waitlist: false };
  }
}

/** Map a registry lifecycle status onto the public ClassItem status. */
export function publicStatusFor(
  registryStatus: VisibilityStatus,
  opts: { registrationEnabled: boolean; fallback: PublicStatus },
): PublicStatus {
  switch (registryStatus) {
    case "coming_soon":
      return "coming-soon";
    case "instructor_wanted":
      return "instructor-wanted";
    case "waitlist":
      return "waitlist";
    case "published":
      // Published but registration switched off must never expose checkout —
      // it degrades to an interest-collecting state, not to a hidden state.
      if (!opts.registrationEnabled) return "coming-soon";
      // Only lifecycle-driven states are promoted to a registering state. A
      // class whose own record does not accept registrations arrives here as
      // "coming-soon" and must stay interest-only — never surface checkout.
      if (opts.fallback === "instructor-wanted" || opts.fallback === "waitlist") {
        return "open";
      }
      return opts.fallback;

    default:
      return opts.fallback;
  }
}

/**
 * The classes-table gate implied by a registry lifecycle status.
 *
 * `class_registry.visibility_status` is the authority; `classes.status` /
 * `classes.hidden` are the storage-level gate that Row Level Security and the
 * public query enforce. They must agree, or a class reads "Published" in admin
 * while parents never see it. Writers use this to keep both in sync.
 */
export function classGateForVisibility(status: VisibilityStatus): {
  status?: "draft" | "coming_soon" | "published" | "archived";
  hidden?: boolean;
} {
  switch (status) {
    case "published":
    case "waitlist":
    case "instructor_wanted":
      return { status: "published", hidden: false };
    case "coming_soon":
      return { status: "coming_soon", hidden: false };
    case "hidden":
      return { hidden: true };
    case "draft":
    case "proposal":
      // A Proposal is admin-only: never public, never registrable.
      return { status: "draft", hidden: true };

    case "archived":
      return { status: "archived" };
    default:
      return {};
  }
}

export interface ClassGateRow {
  status: "draft" | "coming_soon" | "published" | "archived";
  hidden: boolean;
  accepts_registrations?: boolean;
}

export interface VisibilityConflict {
  key: string;
  label: string;
  detail: string;
}

/**
 * Detect configuration that makes the admin badge lie about the public site.
 * Surfaced on admin class cards so a "Published" class can never be silently
 * invisible to parents.
 */
export function detectVisibilityConflicts(
  registryStatus: VisibilityStatus,
  row: ClassGateRow | null | undefined,
  registrationEnabled = true,
): VisibilityConflict[] {
  const out: VisibilityConflict[] = [];
  if (!row) return out;
  const meta = STATUS_META[registryStatus];
  const gate = classGateForVisibility(registryStatus);

  if (meta.publiclyVisible) {
    if (row.hidden) {
      out.push({
        key: "hidden",
        label: "Hidden from public",
        detail: "This class is flagged hidden, so parents cannot see it.",
      });
    }
    if (row.status === "draft" || row.status === "archived") {
      out.push({
        key: "draft",
        label: "Hidden from public",
        detail: `Class record is still "${row.status}", so it is not served to the public site.`,
      });
    } else if (gate.status && row.status !== gate.status) {
      out.push({
        key: "gate",
        label: "Status mismatch",
        detail: `Class record says "${row.status}" but visibility is "${meta.label}".`,
      });
    }
  }

  if (registryStatus === "published") {
    if (!registrationEnabled || row.accepts_registrations === false) {
      out.push({
        key: "interest_only",
        label: "Interest only",
        detail: "Registration is switched off — parents can only join the interest list.",
      });
    }
  }
  return out;
}

/** Composite admin label, e.g. "Published · Hidden from public". */
export function adminStatusLabel(
  registryStatus: VisibilityStatus,
  conflicts: VisibilityConflict[],
): string {
  const base = STATUS_META[registryStatus].label;
  if (conflicts.length === 0) return base;
  const seen = new Set<string>();
  const parts = conflicts
    .map((c) => c.label)
    .filter((l) => (seen.has(l) ? false : (seen.add(l), true)));
  return [base, ...parts].join(" · ");
}

/**
 * Public visibility for a single class. Unknown slugs stay visible (a newly
 * added class is not silently swallowed), matching `useClassRegistry`.
 */
export function isPubliclyVisible(entry: RegistryEntry | undefined): boolean {
  return entry ? STATUS_META[entry.visibility_status].publiclyVisible : true;
}
